import React, { useState, useRef, useEffect } from 'react';
import { Send, ArrowLeft, Loader2, Mic, X, Check, RotateCcw } from 'lucide-react';
import { Message } from '../api/model';
import MarkdownRenderer from './MarkdownRenderer';
import { useChatService, useAudioService } from '../hooks/useServices';
import { useUserDisplay } from '../hooks/useUserDisplay';
import ChatSkeleton from './ChatSkeleton';
import { WaveSurferVisualizer, WaveSurferVisualizerRef } from './WaveSurferVisualizer';
import ConfirmationModal from './ConfirmationModal';
import Toast from './Toast';

interface ChatPageProps {
  companion: {
    id: string;
    name: string;
    image: string;
    description: string;
  };
  onBackToHome: () => void;
}

function ChatPage({ companion, onBackToHome }: ChatPageProps) {
  const chatService = useChatService();
  const audioService = useAudioService();
  const { getUserDisplayName, getUserInitials } = useUserDisplay();
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [tempMessage, setTempMessage] = useState<Message | null>(null);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
    isVisible: boolean;
  }>({
    message: '',
    type: 'info',
    isVisible: false,
  });
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const waveSurferRef = useRef<WaveSurferVisualizerRef>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const showToast = (message: string, type: 'success' | 'error' | 'warning' | 'info') => {
    setToast({ message, type, isVisible: true });
  };

  const hideToast = () => {
    setToast((prev) => ({ ...prev, isVisible: false }));
  };

  const loadChatHistory = async () => {
    try {
      setIsLoadingHistory(true);
      const response = await chatService.getChatHistory(companion.id);
      if (response.success) {
        setMessages(response.data);
      }
    } catch (error) {
      console.error('Failed to load chat history:', error);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load chat history when component mounts
  useEffect(() => {
    loadChatHistory();
  }, [chatService, companion.id]);

  // Focus the textarea when loading is complete
  useEffect(() => {
    if (!isLoadingHistory && !isRecording && !isTranscribing) {
      // Small delay to ensure the textarea is rendered
      const timer = setTimeout(() => {
        textareaRef.current?.focus();
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isLoadingHistory, isRecording, isTranscribing]);

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;

    const messageContent = inputValue;
    setInputValue('');
    setIsTyping(true);

    // Create temporary user message to show immediately
    const tempUserMessage: Message = {
      id: `temp-${Date.now()}`,
      content: messageContent,
      sender: 'user',
      timestamp: new Date().toISOString(),
      user_id: 'current-user',
      companion_id: companion.id,
    };

    // Show the user message immediately
    setTempMessage(tempUserMessage);

    // Scroll to bottom immediately for user message
    scrollToBottom();

    try {
      const response = await chatService.sendMessage({
        companionId: companion.id,
        content: messageContent,
      });

      if (response.success) {
        // Clear temporary message and add the actual messages
        setTempMessage(null);
        const newMessages = [response.data.message];
        if (response.data.companion_response) {
          newMessages.push(response.data.companion_response);
        }
        setMessages((prev) => [...prev, ...newMessages]);
      } else {
        // Handle error - remove temp message and restore input
        setTempMessage(null);
        console.error('Failed to send message:', response.message);
        setInputValue(messageContent);
      }
    } catch (error) {
      // Handle error - remove temp message and restore input
      setTempMessage(null);
      console.error('Error sending message:', error);
      setInputValue(messageContent);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Recording handlers
  const handleRecordingStart = () => {
    console.log('Recording started');
  };

  const handleRecordingStop = async (audioBlob: Blob) => {
    try {
      console.log('Recording stopped, transcribing...');
      setIsTranscribing(true);
      setIsRecording(false);

      // Create a File object from the Blob
      const audioFile = new File([audioBlob], 'recording.webm', { type: 'audio/webm' });

      // Transcribe the audio
      const response = await audioService.transcribeAudio({ audioFile });

      if (response.success && response.data.text) {
        // Set the transcribed text in the input field
        setInputValue(response.data.text);
      } else {
        console.error('Failed to transcribe audio:', response.message);
      }
    } catch (error) {
      console.error('Error transcribing audio:', error);
    } finally {
      setIsTranscribing(false);
    }
  };

  const handleRecordingCancel = () => {
    console.log('Recording cancelled');
    waveSurferRef.current?.cancelRecording();
    setIsRecording(false);
  };

  const handleResetChat = () => {
    if (isResetting) return;
    setShowResetModal(true);
  };

  const handleConfirmReset = async () => {
    try {
      setIsResetting(true);
      const response = await chatService.resetChat(companion.id);

      if (response.success) {
        // Clear all messages from the UI
        setMessages([]);
        setTempMessage(null);
        setShowResetModal(false);
        showToast('Chat reset successfully', 'success');

        // Reload chat history to trigger greeting message
        await loadChatHistory();

        // Ensure we scroll to bottom after reset
        setTimeout(() => {
          scrollToBottom();
        }, 300);
      } else {
        console.error('Failed to reset chat:', response.message);
        showToast('Failed to reset chat. Please try again.', 'error');
      }
    } catch (error) {
      console.error('Error resetting chat:', error);
      showToast('Error resetting chat. Please try again.', 'error');
    } finally {
      setIsResetting(false);
    }
  };

  const handleCancelReset = () => {
    setShowResetModal(false);
  };

  if (isLoadingHistory) {
    return (
      <ChatSkeleton
        companionName={companion.name}
        companionImage={companion.image}
        onBackToHome={onBackToHome}
      />
    );
  }

  return (
    <div className="flex-1 flex flex-col bg-kind-deep h-full">
      {/* Fixed Header */}
      <div className="bg-kind-deep border-b border-gray-300 dark:border-white/10 flex-shrink-0">
        {/* Mobile Header - WhatsApp Style */}
        <div className="lg:hidden flex items-center px-4 py-3">
          <button
            onClick={onBackToHome}
            className="p-2 mr-3 hover:bg-kind-container rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-kind-text-primary" />
          </button>

          <img
            src={companion.image}
            alt={companion.name}
            className="w-10 h-10 rounded-full object-cover mr-3"
          />

          <div className="flex-1">
            <h1 className="text-base font-title font-medium text-kind-text-primary">
              {companion.name}
            </h1>
          </div>

          <button
            onClick={handleResetChat}
            disabled={isResetting}
            className="p-2 hover:bg-kind-container rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Reset chat"
          >
            {isResetting ? (
              <Loader2 className="w-5 h-5 text-kind-text-primary animate-spin" />
            ) : (
              <RotateCcw className="w-5 h-5 text-kind-text-primary" />
            )}
          </button>
        </div>

        {/* Desktop Header */}
        <div className="hidden lg:block px-8 py-6">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col items-center text-center relative">
              <button
                onClick={handleResetChat}
                disabled={isResetting}
                className="absolute top-0 right-0 p-2 hover:bg-kind-container rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Reset chat"
              >
                {isResetting ? (
                  <Loader2 className="w-5 h-5 text-kind-text-primary animate-spin" />
                ) : (
                  <RotateCcw className="w-5 h-5 text-kind-text-primary" />
                )}
              </button>

              <img
                src={companion.image}
                alt={companion.name}
                className="w-20 h-20 rounded-full object-cover border-2 border-kind-purple mb-4"
              />
              <h1 className="text-3xl font-title font-medium text-kind-text-primary mb-2">
                {companion.name}
              </h1>
              <p className="text-base font-body font-light text-kind-text-secondary max-w-2xl leading-relaxed">
                {companion.description}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable Messages Area */}
      <div className="flex-1 overflow-y-auto px-4 lg:px-8 py-4 lg:py-6">
        <div className="max-w-4xl mx-auto space-y-4 lg:space-y-6">
          {/* Render all messages including temporary message */}
          {[...messages, ...(tempMessage ? [tempMessage] : [])].map((message) => {
            if (message.sender === 'companion') {
              // Companion messages - left aligned with companion avatar
              return (
                <div key={message.id} className="flex items-start space-x-3 lg:space-x-4">
                  <img
                    src={companion.image}
                    alt={companion.name}
                    className="w-8 h-8 lg:w-10 lg:h-10 rounded-full object-cover flex-shrink-0"
                  />
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="text-sm lg:text-base text-kind-text-primary font-medium">
                        {companion.name}
                      </span>
                    </div>

                    <div className="bg-kind-graphite rounded-2xl p-3 lg:p-4 text-sm lg:text-base text-kind-text-primary leading-relaxed chat-message">
                      <MarkdownRenderer content={message.content} />
                    </div>
                  </div>
                </div>
              );
            } else {
              // User messages - right aligned with user avatar
              const isTempMessage = message.id.startsWith('temp-');
              return (
                <div key={message.id} className="flex flex-col items-end space-y-2">
                  <div className="flex items-center space-x-2">
                    <span className="text-xs lg:text-sm text-kind-text-secondary">
                      {getUserDisplayName()}
                    </span>
                    <div className="w-6 h-6 lg:w-8 lg:h-8 bg-kind-purple rounded-full flex items-center justify-center text-white text-xs lg:text-sm font-bold">
                      {getUserInitials()}
                    </div>
                  </div>
                  <div
                    className={`rounded-2xl px-3 lg:px-4 py-2 lg:py-3 text-sm lg:text-base text-kind-text-primary max-w-xs lg:max-w-md chat-message ${
                      isTempMessage
                        ? 'bg-kind-graphite opacity-75 border border-kind-purple/30'
                        : 'bg-kind-container'
                    }`}
                  >
                    <MarkdownRenderer content={message.content} />
                    {isTempMessage && (
                      <div className="flex items-center justify-end mt-1">
                        <div className="w-2 h-2 bg-kind-purple/60 rounded-full animate-pulse"></div>
                      </div>
                    )}
                  </div>
                </div>
              );
            }
          })}

          {isTyping && (
            <div className="flex items-start space-x-3 lg:space-x-4">
              <img
                src={companion.image}
                alt={companion.name}
                className="w-8 h-8 lg:w-10 lg:h-10 rounded-full object-cover flex-shrink-0"
              />
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <span className="text-sm lg:text-base text-kind-text-primary font-medium">
                    {companion.name}
                  </span>
                </div>

                <div className="bg-kind-graphite rounded-2xl p-3 lg:p-4 chat-message">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-kind-text-secondary rounded-full animate-bounce"></div>
                    <div
                      className="w-2 h-2 bg-kind-text-secondary rounded-full animate-bounce"
                      style={{ animationDelay: '0.1s' }}
                    ></div>
                    <div
                      className="w-2 h-2 bg-kind-text-secondary rounded-full animate-bounce"
                      style={{ animationDelay: '0.2s' }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Fixed Input */}
      <div className="bg-kind-deep border-t border-gray-300 dark:border-white/10 flex-shrink-0 px-4 lg:px-8 py-4">
        <div className="max-w-4xl mx-auto">
          {isRecording ? (
            /* Recording Interface */
            <div className="bg-kind-graphite rounded-2xl p-3 lg:p-4">
              <div className="flex items-center justify-center space-x-4">
                {/* WaveSurfer visualizer */}
                <div className="flex-1 flex items-center justify-center relative">
                  <WaveSurferVisualizer
                    ref={waveSurferRef}
                    isRecording={isRecording}
                    onRecordingStart={handleRecordingStart}
                    onRecordingStop={handleRecordingStop}
                    onRecordingCancel={handleRecordingCancel}
                    className="w-full"
                  />
                </div>

                {/* Recording controls */}
                <div className="flex items-center space-x-2 flex-shrink-0 relative z-20">
                  {/* Cancel button */}
                  <button
                    onClick={handleRecordingCancel}
                    disabled={isTranscribing}
                    className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-all duration-200 flex-shrink-0 disabled:opacity-50 relative z-20"
                    title="Cancel recording"
                  >
                    <X className="w-4 h-4" />
                  </button>

                  {/* Proceed button */}
                  <button
                    onClick={() => setIsRecording(false)}
                    disabled={isTranscribing}
                    className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-all duration-200 flex-shrink-0 disabled:opacity-50 relative z-20"
                    title="Finish recording"
                  >
                    <Check className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ) : (
            /* Normal Text Input Interface */
            <div className="bg-kind-graphite rounded-2xl p-3 lg:p-4">
              <div className="flex items-end space-x-3 lg:space-x-4">
                <div
                  className={`flex-1 flex gap-2 ${
                    isTranscribing ? 'items-center justify-center' : ''
                  }`}
                >
                  {!isTranscribing && (
                    <textarea
                      ref={textareaRef}
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder={`Message ${companion.name}...`}
                      className="w-full bg-transparent text-sm lg:text-base text-kind-text-primary placeholder-kind-text-secondary resize-none focus:outline-none relative z-10 disabled:opacity-50 disabled:cursor-not-allowed"
                      rows={1}
                      style={{ minHeight: '24px', maxHeight: '120px' }}
                    />
                  )}

                  {/* Show transcribing indicator */}
                  {isTranscribing && (
                    <>
                      <Loader2 className="w-3 h-3 animate-spin" />
                      <span className="text-xs">Transcribing...</span>
                    </>
                  )}
                </div>

                {/* Conditional button: Audio recording when input is empty, Send when input has text */}
                {inputValue.trim() ? (
                  <button
                    onClick={handleSendMessage}
                    disabled={!inputValue.trim() || isTranscribing}
                    className="p-2 bg-kind-purple text-white rounded-full hover:bg-kind-purple/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex-shrink-0"
                    title="Send message"
                  >
                    <Send className="w-4 h-4 lg:w-5 lg:h-5" />
                  </button>
                ) : (
                  <button
                    onClick={() => setIsRecording(true)}
                    disabled={isTranscribing}
                    className="p-2 bg-kind-purple text-white rounded-full hover:bg-kind-purple/90 transition-all duration-200 flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    title={isTranscribing ? 'Transcribing audio...' : 'Start recording'}
                  >
                    <Mic className="w-4 h-4 lg:w-5 lg:h-5" />
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Reset Chat Confirmation Modal */}
      <ConfirmationModal
        isOpen={showResetModal}
        onClose={handleCancelReset}
        onConfirm={handleConfirmReset}
        title="Reset Chat"
        message={`Are you sure you want to reset your chat with ${companion.name}? This will delete all messages and cannot be undone.`}
        confirmText="Reset Chat"
        cancelText="Cancel"
        variant="danger"
        isLoading={isResetting}
      />

      {/* Toast Notifications */}
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />
    </div>
  );
}

export default ChatPage;
